// useFloorStackRealtime — opens a WebSocket (or SSE fallback) to the
// realtime-api worker and applies inbound events to the local snapshot
// state. The hook returns:
//
//   { state: "connecting" | "live" | "fallback" | "offline";
//     lastEventAt: string | null;
//     onEvent: (cb) => void; off }
//
// The container wires `onEvent` to a setState that:
//   - For `snapshot` events: splices the new snapshot into the floor's
//     `latest_snapshot` field of the cached `useFloorStack` data.
//   - For `incident_open` / `incident_ack`: bumps the floor's
//     incident counters by ±1.
//   - For `patrol_event`: refreshes `last_patrol_at` and recolors.
//
// Falls back gracefully:
//   - Dev / offline: hook stays in "offline" state, container relies on
//     the 30 s polling already in `useFloorStack`.
//   - Browsers behind picky proxies blocking WS: hook auto-retries with
//     `Accept: text/event-stream` after 3 failed WS upgrades.

import { useCallback, useEffect, useRef, useState } from "react";
import { resolveApiBase } from "../../../lib/api-client.js";

export type RealtimeKind =
  | "snapshot"
  | "incident_open"
  | "incident_ack"
  | "incident_resolved"
  | "patrol_event"
  | "system";

export interface RealtimeFrame {
  seq: number;
  ts: string;
  tenant_id: string;
  site_id: string;
  correlation_id: string;
  event:
    | { kind: "snapshot"; floor_id: string; snapshot: Record<string, unknown> }
    | { kind: "incident_open"; floor_id: string | null; incident_id: string; severity: string }
    | { kind: "incident_ack"; floor_id: string | null; incident_id: string }
    | { kind: "incident_resolved"; floor_id: string | null; incident_id: string }
    | { kind: "patrol_event"; floor_id: string | null; officer_id: string | null; location_id: string | null; occurred_at: string }
    | { kind: "system"; floor_id: string | null; message: string };
}

export type ConnectionState = "connecting" | "live" | "fallback" | "offline";

interface Options {
  /** When true, the hook stays offline (used in tests / Storybook). */
  disabled?: boolean;
  /** Override transport — useful for forcing SSE in tests. */
  forceSse?: boolean;
}

interface Result {
  connection: ConnectionState;
  lastEventAt: string | null;
  lastSeq: number;
  /** Subscribe to typed events. Returns an off function. */
  onEvent: (handler: (frame: RealtimeFrame) => void) => () => void;
}

const MAX_WS_FAILURES = 3;
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000];

export function useFloorStackRealtime(
  siteId: string | null,
  tenantId: string | null,
  opts: Options = {},
): Result {
  const [connection, setConnection] = useState<ConnectionState>("offline");
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [lastSeq, setLastSeq] = useState(0);

  const handlersRef = useRef<Set<(f: RealtimeFrame) => void>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const wsFailuresRef = useRef(0);
  const attemptRef = useRef(0);

  const onEvent = useCallback((handler: (f: RealtimeFrame) => void) => {
    handlersRef.current.add(handler);
    return () => { handlersRef.current.delete(handler); };
  }, []);

  const dispatchFrame = useCallback((raw: string) => {
    let frame: RealtimeFrame;
    try { frame = JSON.parse(raw) as RealtimeFrame; }
    catch { return; }
    if (!frame || !frame.event || typeof frame.event.kind !== "string") return;
    setLastEventAt(frame.ts ?? new Date().toISOString());
    if (typeof frame.seq === "number") setLastSeq((s) => Math.max(s, frame.seq));
    handlersRef.current.forEach((h) => {
      try { h(frame); } catch { /* swallow handler errors */ }
    });
  }, []);

  useEffect(() => {
    if (opts.disabled || !siteId || !tenantId) {
      setConnection("offline");
      return;
    }

    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      const useSse = opts.forceSse || wsFailuresRef.current >= MAX_WS_FAILURES;
      setConnection("connecting");
      if (useSse) {
        connectSse();
      } else {
        connectWs();
      }
    };

    const connectWs = () => {
      try {
        const wsUrl = buildUrl(siteId, tenantId, "ws");
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.addEventListener("open", () => {
          if (cancelled) return;
          wsFailuresRef.current = 0;
          attemptRef.current = 0;
          setConnection("live");
        });
        ws.addEventListener("message", (ev) => {
          dispatchFrame(typeof ev.data === "string" ? ev.data : "");
        });
        ws.addEventListener("close", () => {
          if (cancelled) return;
          wsRef.current = null;
          wsFailuresRef.current++;
          scheduleReconnect();
        });
        ws.addEventListener("error", () => {
          if (cancelled) return;
          wsFailuresRef.current++;
          try { ws.close(); } catch { /* ignore */ }
        });
      } catch {
        wsFailuresRef.current++;
        scheduleReconnect();
      }
    };

    const connectSse = () => {
      try {
        const sseUrl = buildUrl(siteId, tenantId, "sse");
        const es = new EventSource(sseUrl, { withCredentials: true });
        sseRef.current = es;
        es.addEventListener("open", () => {
          if (cancelled) return;
          setConnection("fallback");
        });
        const onMsg = (ev: MessageEvent) => dispatchFrame(typeof ev.data === "string" ? ev.data : "");
        es.addEventListener("message", onMsg);
        ["snapshot", "incident_open", "incident_ack", "incident_resolved", "patrol_event", "system"].forEach((k) =>
          es.addEventListener(k, onMsg as EventListener),
        );
        es.addEventListener("error", () => {
          if (cancelled) return;
          try { es.close(); } catch { /* ignore */ }
          sseRef.current = null;
          scheduleReconnect();
        });
      } catch {
        scheduleReconnect();
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = RECONNECT_BACKOFF_MS[Math.min(attemptRef.current, RECONNECT_BACKOFF_MS.length - 1)];
      attemptRef.current++;
      setTimeout(() => { if (!cancelled) connect(); }, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* ignore */ } wsRef.current = null; }
      if (sseRef.current) { try { sseRef.current.close(); } catch { /* ignore */ } sseRef.current = null; }
    };
  }, [siteId, tenantId, opts.disabled, opts.forceSse, dispatchFrame]);

  return { connection, lastEventAt, lastSeq, onEvent };
}

function buildUrl(siteId: string, tenantId: string, transport: "ws" | "sse"): string {
  // Host-aware API base (NEXUS group 8): matches the dashboard's current host
  // (nexus → api.nexus, location → api.location), VITE_API_BASE override
  // for dev, nexus production default.
  const apiBase = resolveApiBase();
  const u = new URL(apiBase);
  u.pathname = `/v1/realtime/sites/${encodeURIComponent(siteId)}/floor-stack`;
  u.search = `tenant_id=${encodeURIComponent(tenantId)}`;
  if (transport === "ws") {
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  } else {
    u.searchParams.set("transport", "sse");
  }
  return u.toString();
}
